import {
  Alert,
  AlertIcon,
  Box,
  Button,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Text,
  VStack,
} from '@chakra-ui/react';
import { format } from 'date-fns';
import esLocale from 'date-fns/locale/es';
import { useEffect, useState } from 'react';
import { FiClock, FiMapPin, FiUsers } from 'react-icons/fi';

/**
 * Lo único que falta saber tras elegir el hueco en el calendario.
 *
 * El formulario necesita el número de personas para calcular las horas que
 * puede ofrecer, así que se pregunta antes de abrirlo. De paso sirve de
 * confirmación de lo elegido: sala, día y horario a la vista.
 */

type Props = {
  isOpen: boolean;
  onClose: () => void;
  hueco: {
    salaNombre: string;
    salaId: string;
    /** Aforo de la sala, si está configurado. */
    capacidad?: number;
    /** Plazas de cada franja (`numberBookingsAllow`). */
    cupo?: number;
    /** Si cada persona consume una plaza (`personHasSpecificPosition`). */
    porPersona?: boolean;
    fecha: Date;
    /** Vacía en las zonas de día completo: la hora se elige en el formulario. */
    horaInicio?: string;
    /** Vacío en los espacios que no piden hora de fin. */
    horaFin?: string;
    /** Visitas: duración fija según la configuración del espacio. */
    duracionMinutos?: number;
    /** Zonas de día completo: sillas que quedan ese día. */
    sillasLibres?: number;
  } | null;
  onConfirmar: (personas: number) => void;
};

/** "10:00" + 60 → "11:00". Solo para enseñarlo: la hora de fin no viaja al formulario. */
const finDeVisita = (horaInicio: string, minutos: number): string => {
  const [h, m] = horaInicio.split(':').map(Number);
  const total = h * 60 + m + minutos;
  return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

const CuantasPersonas = ({ isOpen, onClose, hueco, onConfirmar }: Props) => {
  // Vacío al abrir: se escribe el número o se sube con las flechas. Empezar en
  // "1" invita a darle a Continuar sin pensar.
  const [texto, setTexto] = useState('');

  useEffect(() => {
    if (isOpen) setTexto('');
  }, [isOpen]);

  if (!hueco) return null;

  /**
   * Cuánta gente admite de verdad esta sala en una reserva.
   *
   * No basta con el aforo: cuando el espacio asigna una plaza POR PERSONA, el
   * límite es el número de plazas de la franja. Hay salas configuradas así con
   * una sola plaza, que por tanto solo admiten a una persona; sin esta cuenta,
   * se podía elegir el hueco y llegar a un formulario sin ninguna hora que
   * ofrecer, sin explicar por qué.
   */
  const topes = [
    hueco.capacidad && hueco.capacidad > 0 ? hueco.capacidad : undefined,
    hueco.porPersona && hueco.cupo && hueco.cupo > 0 ? hueco.cupo : undefined,
    // Día completo: no más gente que sillas libres quedan; si no, el
    // formulario no ofrecería ninguna hora y no explicaría por qué.
    typeof hueco.sillasLibres === 'number' ? hueco.sillasLibres : undefined,
  ].filter((n): n is number => typeof n === 'number');
  const tope = topes.length ? Math.min(...topes) : undefined;
  const diaCompleto = !hueco.horaInicio;
  const personas = Number(texto);
  const vacio = texto.trim() === '';
  const excede = Boolean(tope) && personas > (tope ?? 0);
  const valido = !vacio && Number.isInteger(personas) && personas >= 1 && !excede;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='sm' isCentered>
      {/* Se apoya sobre el calendario, que sigue detrás: así no se pierde de
          vista el hueco que se acaba de elegir. */}
      <ModalOverlay backdropFilter='blur(3px)' bg='blackAlpha.500' />
      <ModalContent>
        <ModalHeader pb={1}>
          <HStack spacing={2}>
            <Icon as={FiUsers} color='blue.500' />
            <Text fontSize='md'>¿Cuántas personas?</Text>
          </HStack>
        </ModalHeader>

        <ModalBody pb={2}>
          <VStack align='stretch' spacing={3}>
            <Box bg='gray.50' borderRadius='md' px={3} py={2}>
              <HStack spacing={2} fontSize='sm'>
                <Icon as={FiMapPin} color='gray.500' boxSize={3.5} />
                <Text fontWeight='medium'>{hueco.salaNombre}</Text>
              </HStack>
              <HStack spacing={2} fontSize='sm' color='gray.600' mt={1}>
                <Icon as={FiClock} color='gray.500' boxSize={3.5} />
                <Text sx={{ '&::first-letter': { textTransform: 'uppercase' } }}>
                  {format(hueco.fecha, "EEEE d 'de' MMMM", { locale: esLocale })}
                  {diaCompleto ? ' · la hora de llegada se elige en el formulario' : ` · ${hueco.horaInicio}`}
                  {hueco.horaFin ? `–${hueco.horaFin}` : ''}
                  {hueco.horaInicio && !hueco.horaFin && hueco.duracionMinutos
                    ? `–${finDeVisita(hueco.horaInicio, hueco.duracionMinutos)} · visita de ${hueco.duracionMinutos} min`
                    : ''}
                </Text>
              </HStack>
            </Box>

            <NumberInput
              min={1}
              max={tope}
              // `clampValueOnBlur` y `keepWithinRange` hacen el trabajo sucio:
              // ni negativos, ni cero, ni más de lo que cabe en la sala.
              clampValueOnBlur
              keepWithinRange
              value={texto}
              onChange={(valorTexto) => setTexto(valorTexto.replace(/[^0-9]/g, ''))}
              size='lg'
              autoFocus
            >
              <NumberInputField
                placeholder='0'
                textAlign='center'
                fontSize='2xl'
                fontWeight='bold'
                inputMode='numeric'
                _placeholder={{ color: 'gray.300', fontWeight: 'normal' }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && valido) onConfirmar(personas);
                }}
              />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>

            {tope ? (
              <Text fontSize='xs' color={excede ? 'red.600' : 'gray.500'} textAlign='center'>
                {diaCompleto && tope === hueco.sillasLibres
                  ? tope === 1
                    ? 'Queda una sola silla libre ese día.'
                    : `Quedan ${tope} sillas libres ese día.`
                  : tope === 1
                  ? `${hueco.salaNombre} admite una sola persona por reserva.`
                  : excede
                  ? `En ${hueco.salaNombre} caben ${tope}.`
                  : `Caben hasta ${tope}.`}
              </Text>
            ) : null}

            {!vacio && personas < 1 && (
              <Alert status='warning' borderRadius='md' fontSize='sm' py={2}>
                <AlertIcon boxSize={4} />
                Tiene que ser al menos una persona.
              </Alert>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter gap={2}>
          <Button variant='ghost' size='sm' onClick={onClose}>
            Cancelar
          </Button>
          <Button
            size='sm'
            colorScheme='blue'
            isDisabled={!valido}
            onClick={() => onConfirmar(personas)}
          >
            Continuar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CuantasPersonas;
